import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
} from '@material-ui/core'
import moment from 'moment'
import styles from './QuoteBuilderPage.module.css'
import { getProductsByUser } from '../actions/productActions'
import { addProduct as addCatalogProduct, addQuote, createQuotePdf, fetchPcModelInventory, fetchQuote, updateQuote } from '../api'
import useAuthUser from '../hooks/useAuthUser'
import { dollarFormatter, formatCentsToDollars } from '../utils/money'
import { openPdfBlob } from '../utils/pdfDownload'
import { buildQuoteSourceNotes, buildWalkUpChargesFromTotals, parseAddressForShipTo } from '../utils/quoteDraft'
import { formatDateForInput, parseQuotePaste } from '../utils/quoteParser'

const normalizeModel = (value = '') => String(value).trim().toUpperCase()
const INVENTORY_BUFFER_UNITS = 1
const WALK_UP_FEES = {
  '3rd floor': 8,
  '4th floor': 12,
  '5th floor': 18,
  '6th floor': 22,
}
const WALK_UP_FLOORS = Object.keys(WALK_UP_FEES)
const FLOOR_LABEL_BY_NUMBER = {
  3: '3rd floor',
  4: '4th floor',
  5: '5th floor',
  6: '6th floor',
}
const MIXED_WALK_UP_LABEL = 'Multiple walk-ups'
const normalizeClipboardCell = (value = '') =>
  String(value)
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
const REASON_LABEL_REGEX = /reason\s+for\s+purchase\s*:?/i
const WALK_UP_LABEL_REGEX = /walk\s*up\/?elevator\s*:?/i
const METADATA_LABEL_CELL_REGEX = /^(ship\s*to\s*address|reason\s+for\s+purchase|walk\s*up\/?elevator|date\s+required)\s*:?.*$/i

const getInlineMetadataValue = (cell = '', labelRegex, stopRegexes = []) => {
  const match = String(cell).match(labelRegex)
  if (!match) return ''
  let value = String(cell).slice(match.index + match[0].length).replace(/^[:\s-]+/, '').trim()
  stopRegexes.forEach((stopRegex) => {
    const stopMatch = value.match(stopRegex)
    if (stopMatch) value = value.slice(0, stopMatch.index).trim()
  })
  return value
}

const getMetadataValue = (row = [], labelRegex, stopRegexes = []) => {
  for (let index = 0; index < row.length; index += 1) {
    const cell = row[index] || ''
    if (!labelRegex.test(cell)) continue

    const inlineValue = getInlineMetadataValue(cell, labelRegex, stopRegexes)
    if (inlineValue) return inlineValue

    for (let nextIndex = index + 1; nextIndex < row.length; nextIndex += 1) {
      const nextCell = row[nextIndex] || ''
      if (stopRegexes.some((stopRegex) => stopRegex.test(nextCell))) return ''
      if (nextCell) return nextCell.replace(/^[:\s-]+/, '').trim()
    }
  }
  return ''
}

const normalizeMetadataRow = (row = []) => {
  const normalizedRow = row.map(normalizeClipboardCell)
  const hasReason = normalizedRow.some((cell) => REASON_LABEL_REGEX.test(cell))
  const hasWalkUp = normalizedRow.some((cell) => WALK_UP_LABEL_REGEX.test(cell))
  if (!hasReason || !hasWalkUp) return normalizedRow

  return [
    'Reason for purchase:',
    getMetadataValue(normalizedRow, REASON_LABEL_REGEX, [WALK_UP_LABEL_REGEX]) || '',
    'Walk up/Elevator:',
    getMetadataValue(normalizedRow, WALK_UP_LABEL_REGEX) || '',
  ]
}

const normalizeClipboardRows = (rows = []) =>
  rows.map(normalizeMetadataRow)

const getClipboardTableRowsFromHtml = (html = '') => {
  if (!html || typeof DOMParser === 'undefined') return []
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const rows = Array.from(doc.querySelectorAll('tr'))
    .map((row) => Array.from(row.querySelectorAll('th,td')).map((cell) => normalizeClipboardCell(cell.textContent)))
    .filter((row) => row.some(Boolean))

  return normalizeClipboardRows(rows)
}

const getClipboardTableRowsFromText = (text = '') => {
  const lines = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')
  const rows = lines
    .map((line) => line.split('\t').map(normalizeClipboardCell))
    .filter((row) => row.length > 1 && row.some(Boolean))

  return normalizeClipboardRows(rows)
}

const tableRowsToText = (rows = []) =>
  rows.map((row) => row.map(normalizeClipboardCell).join('\t')).join('\n')

const quoteItemsToTableRows = (items = []) => {
  if (!items.length) return []
  return [
    ['Location', 'Quantity', 'Model', 'Description', 'Email Price'],
    ...items.map((item) => [
      item.location || '',
      item.quantity || '',
      item.modelNumber || '',
      item.description || '',
      item.pastedUnitPrice ? dollarFormatter.format(item.pastedUnitPrice) : '',
    ]),
  ]
}

const toQuantity = (value) => {
  const numeric = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(numeric) ? numeric : 0
}

const parseCurrencyDollars = (value) => {
  const numeric = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(numeric) ? numeric : 0
}

const formatCurrencyInput = (value) => {
  const amount = parseCurrencyDollars(value)
  return amount ? dollarFormatter.format(amount) : ''
}

const formatCurrencyToCents = (value) => Math.round(parseCurrencyDollars(value) * 100)

const emptyCatalogProduct = {
  brand: '',
  modelNumber: '',
  cost: '',
  price: '',
  type: '',
  description: '',
}

const isWalkUpValue = (value = '') => /walk\s*up/i.test(String(value || ''))

const getOrdinalFloorLabel = (floorNumber) => {
  const floor = Number(floorNumber)
  if (!Number.isFinite(floor) || floor <= 0) return ''
  if (FLOOR_LABEL_BY_NUMBER[floor]) return FLOOR_LABEL_BY_NUMBER[floor]
  const suffix = floor % 100 >= 11 && floor % 100 <= 13
    ? 'th'
    : ({ 1: 'st', 2: 'nd', 3: 'rd' }[floor % 10] || 'th')
  return `${floor}${suffix} floor`
}

const getFloorFromUnitToken = (token = '') => {
  const digits = String(token || '').match(/\d+/)?.[0] || ''
  if (!digits) return null
  const floorDigit = digits.length >= 2 ? digits[0] : digits
  const floor = Number(floorDigit)
  return Number.isFinite(floor) && floor > 0 ? floor : null
}

const inferWalkUpFloorFromLocation = (location = '') => {
  const text = String(location || '').trim()
  if (!text) return null

  const explicitPatterns = [
    /\b(?:floor|fl|flr)\s*[:#-]?\s*(\d{1,2})\b/i,
    /\b(\d{1,2})(?:st|nd|rd|th)\s*(?:floor|fl|flr)\b/i,
  ]
  for (const pattern of explicitPatterns) {
    const match = text.match(pattern)
    if (match?.[1]) {
      const floor = Number(match[1])
      if (Number.isFinite(floor) && floor > 0) return floor
    }
  }

  const unitMatch = text.match(/\b(?:apt|apartment|unit|suite|ste|room|rm|location|loc)\.?\s*[:#-]?\s*([A-Z]?\s*\d+[A-Z0-9.-]*)/i)
  if (unitMatch?.[1]) return getFloorFromUnitToken(unitMatch[1])

  const hashMatch = text.match(/#\s*([A-Z]?\s*\d+[A-Z0-9.-]*)/i)
  if (hashMatch?.[1]) return getFloorFromUnitToken(hashMatch[1])

  const standaloneUnitMatch = text.match(/\b[A-Z]?\s*\d{2,}[A-Z0-9.-]*\b/i)
  if (standaloneUnitMatch?.[0]) return getFloorFromUnitToken(standaloneUnitMatch[0])

  return null
}

const getLineWalkUpCharge = (line = {}, fallbackFloor = '') => {
  const quantity = Number(line.quantity) || 0
  const inferredFloorNumber = inferWalkUpFloorFromLocation(line.location)
  const inferredFloorLabel = getOrdinalFloorLabel(inferredFloorNumber)
  const chargeableFloor = FLOOR_LABEL_BY_NUMBER[inferredFloorNumber]

  if (chargeableFloor) {
    const rate = WALK_UP_FEES[chargeableFloor] || 0
    return {
      quantity,
      floorNumber: inferredFloorNumber,
      floorLabel: chargeableFloor,
      displayFloorLabel: chargeableFloor,
      rate,
      total: rate * quantity,
      source: 'inferred',
      needsFallback: false,
    }
  }

  if (inferredFloorNumber && inferredFloorNumber < 3) {
    return {
      quantity,
      floorNumber: inferredFloorNumber,
      floorLabel: inferredFloorLabel,
      displayFloorLabel: `${inferredFloorLabel} (no charge)`,
      rate: 0,
      total: 0,
      source: 'inferred-free',
      needsFallback: false,
    }
  }

  if (fallbackFloor) {
    const rate = WALK_UP_FEES[fallbackFloor] || 0
    return {
      quantity,
      floorNumber: inferredFloorNumber,
      floorLabel: fallbackFloor,
      displayFloorLabel: inferredFloorNumber ? `${fallbackFloor} fallback for ${inferredFloorLabel}` : `${fallbackFloor} fallback`,
      rate,
      total: rate * quantity,
      source: inferredFloorNumber ? 'fallback-unsupported' : 'fallback',
      needsFallback: false,
    }
  }

  return {
    quantity,
    floorNumber: inferredFloorNumber,
    floorLabel: '',
    displayFloorLabel: inferredFloorNumber ? `${inferredFloorLabel} needs fallback` : 'Needs floor',
    rate: 0,
    total: 0,
    source: inferredFloorNumber ? 'unsupported' : 'unknown',
    needsFallback: true,
  }
}

const parsePcArrivalDate = (value) => {
  const parsed = moment(String(value || '').trim(), ['M/D/YY', 'M/D/YYYY', 'MM/DD/YY', 'MM/DD/YYYY', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed : null
}

const formatPcArrivalDate = (value) => {
  const parsed = parsePcArrivalDate(value)
  return parsed ? parsed.format('MM/DD/YYYY') : value || 'N/A'
}

const getTomorrowDeliveryMoment = () => moment().add(1, 'day').startOf('day')

const getDeliveryMomentAfterArrival = (arrivalMoment) => {
  if (!arrivalMoment?.isValid?.()) return null
  const dayAfterArrival = arrivalMoment.clone().add(1, 'day').startOf('day')
  return moment.max(dayAfterArrival, getTomorrowDeliveryMoment())
}

const formatQuoteDeliveryDate = (label = '') => {
  const value = String(label || '').trim()
  if (!value) return 'TBD'
  if (/available now/i.test(value)) return 'available now'
  if (/review|lookup|model|not enough/i.test(value)) return 'TBD'

  const parsed = moment(value, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', 'M/D/YY'], true)
  if (parsed.isValid()) return parsed.format('dddd, MMMM D')
  return value
}

const getQuoteDeliverySentence = (decision = {}) => {
  if (decision?.noDate) {
    const reason = String(decision.reason || '').trim()
    return reason ? `Earliest Free Delivery: TBD - ${reason}` : 'Earliest Free Delivery: TBD'
  }
  return `Earliest Free Delivery: ${formatQuoteDeliveryDate(decision?.label)}`
}

const getDateInputFromDeliveryDecision = (decision = {}) => {
  if (decision?.status === 'manual-date' && decision?.dateInput) return decision.dateInput
  if (!['scheduled', 'available-now'].includes(decision?.status) || !decision?.label) return ''
  const parsed = moment(decision.label, ['MM/DD/YYYY', 'M/D/YYYY', 'M/D/YY', 'YYYY-MM-DD'], true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

const getCandidateModelNumber = (candidate) => normalizeModel(
  typeof candidate === 'string'
    ? candidate
    : candidate?.modelNumber || candidate?.model || candidate?.modelNo || candidate?.id || ''
)

const escapeRegex = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const quoteItemToTableRow = (item = {}) => [
  item.location || '',
  item.quantity || '',
  item.modelNumber || '',
  item.description || item.productDescription || '',
  item.pastedUnitPrice ? dollarFormatter.format(item.pastedUnitPrice) : '',
]

const getScheduledDeliveryRows = (lookupData = {}) => {
  const rows = lookupData.scheduledOnOrderRows?.length
    ? lookupData.scheduledOnOrderRows
    : lookupData.onOrderRows || []

  return rows
    .filter((row) => String(row?.arrivalType || 'scheduled').trim().toLowerCase() === 'scheduled')
    .map((row) => ({
      ...row,
      planQty: toQuantity(row?.planQty),
      arrivalMoment: parsePcArrivalDate(row?.arrival),
    }))
    .sort((a, b) => {
      if (!a.arrivalMoment && !b.arrivalMoment) return 0
      if (!a.arrivalMoment) return 1
      if (!b.arrivalMoment) return -1
      return a.arrivalMoment.valueOf() - b.arrivalMoment.valueOf()
    })
}

const buildAvailabilityProjection = (request, lookup) => {
  const requestedQuantity = toQuantity(request?.quantity)
  const targetQuantity = requestedQuantity + INVENTORY_BUFFER_UNITS

  if (!lookup) {
    return {
      modelNumber: request.modelNumber,
      requestedQuantity,
      targetQuantity,
      status: 'not-checked',
      statusLabel: 'Not checked',
      earliestLabel: 'Run lookup',
      currentAvailable: null,
      projectedRows: [],
    }
  }

  if (lookup.error) {
    return {
      modelNumber: request.modelNumber,
      requestedQuantity,
      targetQuantity,
      status: 'error',
      statusLabel: 'Lookup failed',
      earliestLabel: lookup.error,
      currentAvailable: null,
      projectedRows: [],
    }
  }

  const data = lookup.data || {}
  if (data.modelCandidates?.length && !data.inventorySummary) {
    return {
      modelNumber: request.modelNumber,
      requestedQuantity,
      targetQuantity,
      status: 'candidate',
      statusLabel: 'Select exact model',
      earliestLabel: 'Candidate list returned',
      currentAvailable: null,
      projectedRows: [],
      candidates: data.modelCandidates,
    }
  }

  const totals = data.inventorySummary?.totals || {}
  const currentAvailable = toQuantity(totals.balance)
  const scheduledRows = getScheduledDeliveryRows(data)
  let projectedAvailable = currentAvailable
  let earliestRow = currentAvailable >= targetQuantity ? { arrival: 'Available now', arrivalMoment: null } : null

  const projectedRows = scheduledRows.map((row) => {
    projectedAvailable += toQuantity(row.planQty)
    const meetsTarget = projectedAvailable >= targetQuantity
    const projectedRow = {
      ...row,
      projectedAvailable,
      supportsTarget: !earliestRow && meetsTarget,
    }
    if (!earliestRow && meetsTarget) earliestRow = row
    return projectedRow
  })

  if (currentAvailable >= targetQuantity) {
    const deliveryMoment = getTomorrowDeliveryMoment()
    return {
      modelNumber: request.modelNumber,
      requestedQuantity,
      targetQuantity,
      status: 'available-now',
      statusLabel: 'Available now',
      earliestLabel: deliveryMoment.format('MM/DD/YYYY'),
      earliestMoment: deliveryMoment,
      currentAvailable,
      totals,
      projectedRows,
    }
  }

  if (earliestRow) {
    const inventoryArrivalMoment = earliestRow.arrivalMoment || parsePcArrivalDate(earliestRow.arrival)
    const deliveryMoment = getDeliveryMomentAfterArrival(inventoryArrivalMoment)
    return {
      modelNumber: request.modelNumber,
      requestedQuantity,
      targetQuantity,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      earliestLabel: deliveryMoment ? deliveryMoment.format('MM/DD/YYYY') : formatPcArrivalDate(earliestRow.arrival),
      earliestMoment: deliveryMoment,
      inventoryArrivalLabel: formatPcArrivalDate(earliestRow.arrival),
      currentAvailable,
      totals,
      projectedRows,
    }
  }

  return {
    modelNumber: request.modelNumber,
    requestedQuantity,
    targetQuantity,
    status: 'needs-review',
    statusLabel: 'Needs review',
    earliestLabel: 'Not enough in next scheduled deliveries',
    earliestMoment: null,
    currentAvailable,
    totals,
    projectedRows,
  }
}

const QuoteBuilderPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { id: quoteIdParam } = useParams()
  const authData = useAuthUser()
  const pasteTargetRef = useRef(null)
  const pasteInputRef = useRef(null)
  const autoLookupKeyRef = useRef('')
  const lookupRequestTokenRef = useRef({})
  const { products = [], isLoading } = useSelector((state) => state.products || {})
  const [rawText, setRawText] = useState('')
  const [parsed, setParsed] = useState(() => parseQuotePaste(''))
  const [pastedTableRows, setPastedTableRows] = useState([])
  const [fields, setFields] = useState({
    address: '',
    dateAcquired: formatDateForInput(new Date()),
    dateRequired: '',
    reasonForPurchase: '',
    deliveryNote: '',
    walkUp: '',
    deliveryFee: '',
    earliestFreeDeliveryDate: '',
    deliveryNoDate: false,
    deliveryNoDateReason: '',
  })
  const [walkUpFloor, setWalkUpFloor] = useState('')
  const [inventoryLookups, setInventoryLookups] = useState({})
  const [inventoryLoadingByModel, setInventoryLoadingByModel] = useState({})
  const [savedQuoteId, setSavedQuoteId] = useState('')
  const [savedQuoteNumber, setSavedQuoteNumber] = useState('')
  const [savedQuoteStatus, setSavedQuoteStatus] = useState('')
  const [saveStatus, setSaveStatus] = useState('')
  const [isSavingQuote, setIsSavingQuote] = useState(false)
  const [isGeneratingQuotePdf, setIsGeneratingQuotePdf] = useState(false)
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false)
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false)
  const [catalogProductDialogOpen, setCatalogProductDialogOpen] = useState(false)
  const [isSavingCatalogProduct, setIsSavingCatalogProduct] = useState(false)
  const [newQuoteItem, setNewQuoteItem] = useState({
    location: '',
    quantity: '1',
    modelNumber: '',
    description: '',
    unitPrice: '',
  })
  const [newCatalogProduct, setNewCatalogProduct] = useState(emptyCatalogProduct)
  const [emailText, setEmailText] = useState('')
  const [emailTextEdited, setEmailTextEdited] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    const userId = authData?.result?._id || authData?.result?.googleId
    if (userId) dispatch(getProductsByUser({ search: userId }))
  }, [authData, dispatch, location])

  useEffect(() => {
    if (!authData) history.push('/login')
  }, [authData, history])

  const hydrateSavedQuote = useCallback((quote) => {
    if (!quote) return
    const nextRawText = quote.rawText || ''
    const nextParsed = parseQuotePaste(nextRawText)
    const savedItems = (quote.items || []).map((item) => ({
      location: item.location || '',
      quantity: item.quantity || 1,
      modelNumber: item.modelNumber || '',
      description: item.description || item.productDescription || '',
      pastedUnitPrice: item.pastedUnitPrice || 0,
    }))

    setRawText(nextRawText)
    setParsed({
      ...nextParsed,
      dateRequired: quote.fields?.dateRequired || nextParsed.dateRequired,
      dateRequiredInput: quote.fields?.dateRequiredInput || nextParsed.dateRequiredInput,
      deliveryNote: quote.fields?.deliveryNote || nextParsed.deliveryNote,
      reasonForPurchase: quote.fields?.reasonForPurchase || nextParsed.reasonForPurchase,
      walkUp: quote.fields?.walkUp || nextParsed.walkUp,
      items: savedItems.length ? savedItems : nextParsed.items,
    })
    setPastedTableRows(quote.pastedTableRows?.length ? quote.pastedTableRows : quoteItemsToTableRows(savedItems))
    setFields({
      address: quote.fields?.address || '',
      dateAcquired: quote.fields?.dateAcquired || formatDateForInput(new Date()),
      dateRequired: quote.fields?.dateRequired || '',
      reasonForPurchase: quote.fields?.reasonForPurchase || '',
      deliveryNote: quote.fields?.deliveryNote || '',
      walkUp: quote.fields?.walkUp || '',
      deliveryFee: quote.fields?.deliveryFee !== undefined && quote.fields?.deliveryFee !== null && quote.fields?.deliveryFee !== ''
        ? formatCurrencyInput(quote.fields.deliveryFee)
        : (quote.totals?.deliveryFee ? dollarFormatter.format(quote.totals.deliveryFee) : ''),
      earliestFreeDeliveryDate: quote.fields?.earliestFreeDeliveryDate || '',
      deliveryNoDate: Boolean(quote.fields?.deliveryNoDate),
      deliveryNoDateReason: quote.fields?.deliveryNoDateReason || '',
    })
    setWalkUpFloor(quote.walkUpFloor || '')
    setInventoryLookups({})
    setInventoryLoadingByModel({})
    autoLookupKeyRef.current = ''
    setSavedQuoteId(quote._id || '')
    setSavedQuoteNumber(quote.quoteNumber || '')
    setSavedQuoteStatus(quote.status || 'Draft')
    setEmailTextEdited(false)
    setMessage('')
  }, [])

  useEffect(() => {
    if (!quoteIdParam) {
      setSavedQuoteId('')
      setSavedQuoteNumber('')
      setSavedQuoteStatus('')
      return
    }

    let isMounted = true
    fetchQuote(quoteIdParam)
      .then(({ data }) => {
        if (isMounted) hydrateSavedQuote(data)
      })
      .catch((error) => {
        if (isMounted) setMessage(error?.response?.data?.message || 'Unable to load quote.')
      })

    return () => {
      isMounted = false
    }
  }, [hydrateSavedQuote, quoteIdParam])

  const productByModel = useMemo(() => {
    const map = new Map()
    ;(products || []).forEach((product) => {
      const model = normalizeModel(product?.modelNumber)
      if (model) map.set(model, product)
    })
    return map
  }, [products])

  const quoteLines = useMemo(() => (
    (parsed.items || []).map((item) => {
      const modelNumber = normalizeModel(item.modelNumber)
      const matchedProduct = productByModel.get(modelNumber)
      const catalogPrice = matchedProduct?.price !== undefined && matchedProduct?.price !== null
        ? formatCentsToDollars(matchedProduct.price)
        : null
      const catalogCost = matchedProduct?.cost !== undefined && matchedProduct?.cost !== null
        ? formatCentsToDollars(matchedProduct.cost)
        : ''
      const unitPrice = catalogPrice !== null ? catalogPrice : (item.pastedUnitPrice || 0)
      const quantity = Number(item.quantity) || 1

      return {
        ...item,
        modelNumber,
        matchedProduct,
        productDescription: matchedProduct?.description || '',
        unitPrice,
        unitCost: catalogCost,
        lineTotal: unitPrice * quantity,
        matchStatus: matchedProduct ? 'matched' : 'missing',
      }
    })
  ), [parsed.items, productByModel])

  const inventoryRequests = useMemo(() => {
    const map = new Map()
    quoteLines.forEach((line) => {
      if (!line.modelNumber) return
      const existing = map.get(line.modelNumber) || {
        modelNumber: line.modelNumber,
        quantity: 0,
        description: line.productDescription || line.description || '',
      }
      existing.quantity += Number(line.quantity) || 0
      if (!existing.description) existing.description = line.productDescription || line.description || ''
      map.set(line.modelNumber, existing)
    })
    return Array.from(map.values())
  }, [quoteLines])

  const inventoryRequestKey = useMemo(() => (
    inventoryRequests.map((request) => `${request.modelNumber}:${request.quantity}`).join('|')
  ), [inventoryRequests])

  const requiresWalkUp = useMemo(() => isWalkUpValue(fields.walkUp), [fields.walkUp])

  const totals = useMemo(() => {
    const itemCount = quoteLines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0)
    const itemSubtotal = quoteLines.reduce((sum, line) => sum + (Number(line.lineTotal) || 0), 0)
    const walkUpLines = requiresWalkUp
      ? quoteLines.map((line) => ({
          ...getLineWalkUpCharge(line, walkUpFloor),
          location: line.location || '',
          modelNumber: line.modelNumber,
        }))
      : []
    const walkUpTotal = walkUpLines.reduce((sum, line) => sum + (Number(line.total) || 0), 0)
    const walkUpBreakdownMap = new Map()
    walkUpLines.forEach((line) => {
      const key = line.floorLabel || line.displayFloorLabel || 'Needs floor'
      const current = walkUpBreakdownMap.get(key) || {
        floorLabel: line.floorLabel,
        displayFloorLabel: line.displayFloorLabel,
        rate: line.rate,
        quantity: 0,
        total: 0,
        needsFallback: line.needsFallback,
        source: line.source,
      }
      current.quantity += Number(line.quantity) || 0
      current.total += Number(line.total) || 0
      current.needsFallback = current.needsFallback || line.needsFallback
      walkUpBreakdownMap.set(key, current)
    })
    const walkUpBreakdown = Array.from(walkUpBreakdownMap.values()).sort((a, b) => {
      if (a.needsFallback && !b.needsFallback) return 1
      if (!a.needsFallback && b.needsFallback) return -1
      return (a.rate || 0) - (b.rate || 0)
    })
    const chargedWalkUpBreakdown = walkUpBreakdown.filter((row) => Number(row.total) > 0)
    const unresolvedWalkUpQuantity = walkUpLines
      .filter((line) => line.needsFallback)
      .reduce((sum, line) => sum + (Number(line.quantity) || 0), 0)
    const fallbackWalkUpQuantity = walkUpLines
      .filter((line) => line.source === 'fallback' || line.source === 'fallback-unsupported')
      .reduce((sum, line) => sum + (Number(line.quantity) || 0), 0)
    const walkUpRate = chargedWalkUpBreakdown.length === 1 ? chargedWalkUpBreakdown[0].rate : 0
    const walkUpLabel = !requiresWalkUp
      ? ''
      : chargedWalkUpBreakdown.length === 1 && !fallbackWalkUpQuantity
        ? chargedWalkUpBreakdown[0].floorLabel
        : chargedWalkUpBreakdown.length || fallbackWalkUpQuantity
          ? MIXED_WALK_UP_LABEL
          : ''
    const deliveryFee = parseCurrencyDollars(fields.deliveryFee)
    const subtotal = itemSubtotal + walkUpTotal + deliveryFee
    const missingModels = quoteLines
      .filter((line) => !line.matchedProduct)
      .map((line) => line.modelNumber)

    return {
      itemCount,
      itemSubtotal,
      walkUpRate,
      walkUpTotal,
      walkUpLabel,
      walkUpLines,
      walkUpBreakdown,
      unresolvedWalkUpQuantity,
      fallbackWalkUpQuantity,
      deliveryFee,
      subtotal,
      missingModels,
    }
  }, [quoteLines, requiresWalkUp, walkUpFloor, fields.deliveryFee])

  const availabilityProjections = useMemo(() => (
    inventoryRequests.map((request) => buildAvailabilityProjection(request, inventoryLookups[request.modelNumber]))
  ), [inventoryRequests, inventoryLookups])

  const availabilityProjectionByModel = useMemo(() => {
    const map = new Map()
    availabilityProjections.forEach((projection) => {
      const model = normalizeModel(projection.modelNumber)
      if (model) map.set(model, projection)
    })
    return map
  }, [availabilityProjections])

  const deliveryDecision = useMemo(() => {
    if (!inventoryRequests.length) return { label: 'No models yet', status: 'empty' }
    if (availabilityProjections.some((projection) => projection.status === 'not-checked')) {
      return { label: 'Run inventory lookup', status: 'not-checked' }
    }
    if (availabilityProjections.some((projection) => ['error', 'candidate', 'needs-review'].includes(projection.status))) {
      return { label: 'Needs review', status: 'needs-review' }
    }

    const deliveryMoments = availabilityProjections
      .map((projection) => projection.earliestMoment)
      .filter(Boolean)
    if (!deliveryMoments.length) return { label: 'Needs review', status: 'needs-review' }

    const latestMoment = deliveryMoments.reduce((latest, current) => (
      current.valueOf() > latest.valueOf() ? current : latest
    ), deliveryMoments[0])
    const allAvailableNow = availabilityProjections.every((projection) => projection.status === 'available-now')

    return {
      label: latestMoment.format('MM/DD/YYYY'),
      status: allAvailableNow ? 'available-now' : 'scheduled',
    }
  }, [availabilityProjections, inventoryRequests.length])

  const derivedDeliveryDateInput = useMemo(
    () => getDateInputFromDeliveryDecision(deliveryDecision),
    [deliveryDecision]
  )

  const effectiveDeliveryDecision = useMemo(() => {
    if (fields.deliveryNoDate) {
      return {
        label: fields.deliveryNoDateReason || 'TBD',
        status: 'no-date',
        noDate: true,
        reason: fields.deliveryNoDateReason || '',
      }
    }

    const selectedDate = fields.earliestFreeDeliveryDate || derivedDeliveryDateInput
    if (selectedDate) {
      return {
        label: moment(selectedDate).format('MM/DD/YYYY'),
        status: fields.earliestFreeDeliveryDate ? 'manual-date' : deliveryDecision.status,
        dateInput: selectedDate,
        override: Boolean(fields.earliestFreeDeliveryDate),
      }
    }

    return deliveryDecision
  }, [
    deliveryDecision,
    derivedDeliveryDateInput,
    fields.deliveryNoDate,
    fields.deliveryNoDateReason,
    fields.earliestFreeDeliveryDate,
  ])

  const deliverySentence = useMemo(
    () => getQuoteDeliverySentence(effectiveDeliveryDecision),
    [effectiveDeliveryDecision]
  )

  const generatedEmailText = useMemo(() => {
    if (!quoteLines.length) return ''
    const itemLines = quoteLines.map((line) => {
      const quantity = Number(line.quantity) || 0
      const unitPrice = Number(line.unitPrice) || 0
      return `${quantity} ${line.modelNumber} $${unitPrice.toFixed(2)}`
    })

    const summaryLines = []
    if (Number(totals.deliveryFee || 0) > 0) {
      summaryLines.push(`Delivery Fee $${Number(totals.deliveryFee || 0).toFixed(2)}`)
    }
    summaryLines.push(`Subtotal $${Number(totals.subtotal || 0).toFixed(2)}`)

    return [
      ...itemLines,
      '',
      ...summaryLines,
      '',
      deliverySentence,
    ].join('\n')
  }, [deliverySentence, quoteLines, totals.deliveryFee, totals.subtotal])

  useEffect(() => {
    if (!emailTextEdited) setEmailText(generatedEmailText)
  }, [emailTextEdited, generatedEmailText])

  useEffect(() => {
    if (!requiresWalkUp && walkUpFloor) setWalkUpFloor('')
  }, [requiresWalkUp, walkUpFloor])

  const syncPastedTableRows = (nextRows) => {
    setPastedTableRows(nextRows)
    setRawText(tableRowsToText(nextRows))
    setSaveStatus('')
  }

  const updatePastedTableCell = (rowIndex, cellIndex, value) => {
    const nextRows = pastedTableRows.map((row, currentRowIndex) => (
      currentRowIndex === rowIndex
        ? row.map((cell, currentCellIndex) => (currentCellIndex === cellIndex ? value : cell))
        : row
    ))
    syncPastedTableRows(nextRows)
  }

  const appendQuoteItemToTable = (item) => {
    const itemRow = quoteItemToTableRow(item)
    if (!pastedTableRows.length) {
      syncPastedTableRows(quoteItemsToTableRows([item]))
      return
    }
    syncPastedTableRows([...pastedTableRows, itemRow])
  }

  const replaceModelInPastedTable = (originalModel, selectedModel) => {
    const original = normalizeModel(originalModel)
    const selected = normalizeModel(selectedModel)
    if (!original || !selected || !pastedTableRows.length) return
    const exactModelRegex = new RegExp(`\\b${escapeRegex(original)}\\b`, 'i')
    const nextRows = pastedTableRows.map((row) => (
      row.map((cell) => String(cell || '').replace(exactModelRegex, selected))
    ))
    syncPastedTableRows(nextRows)
  }

  const applyParsedQuote = (nextRawText, tableRows = pastedTableRows) => {
    const nextParsed = parseQuotePaste(nextRawText)
    setParsed(nextParsed)
    setPastedTableRows(tableRows.length ? tableRows : quoteItemsToTableRows(nextParsed.items))
    setFields({
      address: nextParsed.address || '',
      dateAcquired: nextParsed.dateAcquiredInput || formatDateForInput(new Date()),
      dateRequired: nextParsed.dateRequired || nextParsed.deliveryNote || '',
      reasonForPurchase: nextParsed.reasonForPurchase || '',
      deliveryNote: nextParsed.deliveryNote || '',
      walkUp: nextParsed.walkUp || '',
      deliveryFee: '',
      earliestFreeDeliveryDate: '',
      deliveryNoDate: false,
      deliveryNoDateReason: '',
    })
    setWalkUpFloor('')
    setInventoryLookups({})
    setInventoryLoadingByModel({})
    autoLookupKeyRef.current = ''
    setEmailTextEdited(false)
    setSaveStatus('')
    setMessage(nextParsed.items.length ? '' : 'No model rows were found. Check the pasted text and try again.')
  }

  const extractQuote = () => {
    const nextRawText = pastedTableRows.length ? tableRowsToText(pastedTableRows) : rawText
    setRawText(nextRawText)
    applyParsedQuote(nextRawText, pastedTableRows)
  }

  const applyPastedContent = (text = '', html = '') => {
    const htmlRows = getClipboardTableRowsFromHtml(html)
    const textRows = getClipboardTableRowsFromText(text)
    const tableRows = htmlRows.length ? htmlRows : textRows

    const nextRawText = text || tableRowsToText(tableRows)
    setRawText(nextRawText)
    applyParsedQuote(nextRawText, tableRows)
  }

  const handlePaste = (event) => {
    const clipboard = event.clipboardData
    if (!clipboard) return

    event.preventDefault()
    event.stopPropagation()
    applyPastedContent(clipboard.getData('text/plain'), clipboard.getData('text/html'))
    if (pasteInputRef.current) pasteInputRef.current.value = ''
  }

  const handlePasteInputChange = (event) => {
    const nextText = event.target.value
    if (!nextText.trim()) return

    applyPastedContent(nextText)
    event.target.value = ''
  }

  const clearQuote = () => {
    setRawText('')
    setParsed(parseQuotePaste(''))
    setPastedTableRows([])
    setFields({
      address: '',
      dateAcquired: formatDateForInput(new Date()),
      dateRequired: '',
      reasonForPurchase: '',
      deliveryNote: '',
      walkUp: '',
      deliveryFee: '',
      earliestFreeDeliveryDate: '',
      deliveryNoDate: false,
      deliveryNoDateReason: '',
    })
    setWalkUpFloor('')
    setInventoryLookups({})
    setInventoryLoadingByModel({})
    autoLookupKeyRef.current = ''
    setSavedQuoteId('')
    setSavedQuoteNumber('')
    setSavedQuoteStatus('')
    setSaveStatus('')
    setEmailText('')
    setEmailTextEdited(false)
    setMessage('')
    if (quoteIdParam) history.replace('/quote-builder')
  }

  const lookupModelInventory = useCallback(async (modelNumber) => {
    const normalizedModel = normalizeModel(modelNumber)
    if (!normalizedModel) return

    const requestToken = `${Date.now()}-${Math.random()}`
    lookupRequestTokenRef.current[normalizedModel] = requestToken

    setInventoryLoadingByModel((prev) => ({ ...prev, [normalizedModel]: 'active' }))
    try {
      const { data } = await fetchPcModelInventory(normalizedModel)
      if (lookupRequestTokenRef.current[normalizedModel] !== requestToken) return
      setInventoryLookups((prev) => ({
        ...prev,
        [normalizedModel]: {
          data,
          error: null,
          checkedAt: new Date().toISOString(),
        },
      }))
    } catch (error) {
      if (lookupRequestTokenRef.current[normalizedModel] !== requestToken) return
      setInventoryLookups((prev) => ({
        ...prev,
        [normalizedModel]: {
          data: null,
          error: error?.response?.data?.message || error?.message || 'PC Richard inventory lookup failed',
          checkedAt: new Date().toISOString(),
        },
      }))
    } finally {
      if (lookupRequestTokenRef.current[normalizedModel] === requestToken) {
        setInventoryLoadingByModel((prev) => ({ ...prev, [normalizedModel]: false }))
      }
    }
  }, [])

  const lookupAllInventory = useCallback(async () => {
    const models = inventoryRequests
      .map((request) => normalizeModel(request.modelNumber))
      .filter(Boolean)

    if (!models.length) return

    const requestToken = `${Date.now()}-${Math.random()}`
    models.forEach((model) => {
      lookupRequestTokenRef.current[model] = requestToken
    })

    setInventoryLookups((prev) => {
      const next = { ...prev }
      models.forEach((model) => {
        delete next[model]
      })
      return next
    })
    setInventoryLoadingByModel((prev) => (
      models.reduce((next, model) => ({ ...next, [model]: 'queued' }), { ...prev })
    ))

    for (const model of models) {
      if (lookupRequestTokenRef.current[model] !== requestToken) continue

      try {
        setInventoryLoadingByModel((prev) => ({ ...prev, [model]: 'active' }))
        const { data } = await fetchPcModelInventory(model)
        if (lookupRequestTokenRef.current[model] !== requestToken) continue
        setInventoryLookups((prev) => ({
          ...prev,
          [model]: {
            data,
            error: null,
            checkedAt: new Date().toISOString(),
          },
        }))
      } catch (error) {
        if (lookupRequestTokenRef.current[model] !== requestToken) continue
        setInventoryLookups((prev) => ({
          ...prev,
          [model]: {
            data: null,
            error: error?.response?.data?.message || error?.message || 'PC Richard inventory lookup failed',
            checkedAt: new Date().toISOString(),
          },
        }))
      } finally {
        if (lookupRequestTokenRef.current[model] === requestToken) {
          setInventoryLoadingByModel((prev) => ({ ...prev, [model]: false }))
        }
      }
    }
  }, [inventoryRequests])

  const selectModelCandidate = (originalModel, selectedModel) => {
    const original = normalizeModel(originalModel)
    const selected = normalizeModel(selectedModel)
    if (!original || !selected || original === selected) return

    setParsed((prev) => ({
      ...prev,
      items: (prev.items || []).map((item) => (
        normalizeModel(item.modelNumber) === original
          ? { ...item, modelNumber: selected }
          : item
      )),
    }))
    replaceModelInPastedTable(original, selected)
    setInventoryLookups((prev) => {
      const next = { ...prev }
      delete next[original]
      delete next[selected]
      return next
    })
    setInventoryLoadingByModel((prev) => ({
      ...prev,
      [original]: false,
      [selected]: false,
    }))
    autoLookupKeyRef.current = ''
    setEmailTextEdited(false)
    lookupModelInventory(selected)
  }

  const closeAddItemDialog = () => {
    setAddItemDialogOpen(false)
    setNewQuoteItem({
      location: '',
      quantity: '1',
      modelNumber: '',
      description: '',
      unitPrice: '',
    })
  }

  const addQuoteItem = () => {
    const modelNumber = normalizeModel(newQuoteItem.modelNumber)
    if (!modelNumber) {
      setMessage('Model number is required to add a product.')
      return
    }

    const item = {
      location: newQuoteItem.location || '',
      quantity: toQuantity(newQuoteItem.quantity) || 1,
      modelNumber,
      description: newQuoteItem.description || '',
      pastedUnitPrice: Number(String(newQuoteItem.unitPrice || '').replace(/[^0-9.-]/g, '')) || 0,
    }

    setParsed((prev) => ({
      ...prev,
      items: [...(prev.items || []), item],
    }))
    appendQuoteItemToTable(item)
    setSaveStatus('')
    setMessage('')
    setEmailTextEdited(false)
    closeAddItemDialog()
  }

  const closeCatalogProductDialog = () => {
    setCatalogProductDialogOpen(false)
    setNewCatalogProduct(emptyCatalogProduct)
  }

  const saveCatalogProduct = async () => {
    const modelNumber = normalizeModel(newCatalogProduct.modelNumber)
    if (!modelNumber) {
      setMessage('Model number is required to add a product.')
      return
    }

    const userId = authData?.result?._id || authData?.result?.googleId
    setIsSavingCatalogProduct(true)
    try {
      await addCatalogProduct({
        brand: newCatalogProduct.brand || '',
        modelNumber,
        cost: formatCurrencyToCents(newCatalogProduct.cost),
        price: formatCurrencyToCents(newCatalogProduct.price),
        type: newCatalogProduct.type || '',
        description: newCatalogProduct.description || '',
        userId: userId ? [userId] : [],
      })
      if (userId) dispatch(getProductsByUser({ search: userId }))
      setSaveStatus(`Added ${modelNumber} to products.`)
      setMessage('')
      closeCatalogProductDialog()
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Unable to add product.')
    } finally {
      setIsSavingCatalogProduct(false)
    }
  }

  const copyEmailText = async () => {
    try {
      await navigator.clipboard.writeText(emailText)
      setSaveStatus('Email text copied.')
    } catch (error) {
      setMessage('Unable to copy email text. You can still select and copy it manually.')
    }
  }

  useEffect(() => {
    if (!inventoryRequestKey) {
      autoLookupKeyRef.current = ''
      return
    }
    if (autoLookupKeyRef.current === inventoryRequestKey) return

    autoLookupKeyRef.current = inventoryRequestKey
    lookupAllInventory()
  }, [inventoryRequestKey, lookupAllInventory])

  const inventoryLookupInProgress = inventoryRequests.some((request) => inventoryLoadingByModel[request.modelNumber])

  const getProjectionStatusClass = (status) => {
    if (['available-now', 'scheduled'].includes(status)) return styles.statusGood
    if (status === 'not-checked') return styles.statusNeutral
    return styles.statusReview
  }

  const getLineAvailabilityPill = (line = {}) => {
    const model = normalizeModel(line.modelNumber)
    const lookupState = inventoryLoadingByModel[model]
    if (lookupState === 'active' || lookupState === true) return { label: 'Checking', className: styles.statusNeutral }
    if (lookupState === 'queued') return { label: 'Queued', className: styles.statusNeutral }

    const projection = availabilityProjectionByModel.get(model)
    if (!projection || projection.status === 'not-checked') return { label: 'Not checked', className: styles.statusNeutral }
    if (projection.status === 'available-now') return { label: 'Available', className: styles.matched }
    if (projection.status === 'scheduled') return { label: 'Scheduled', className: styles.scheduled }
    if (projection.status === 'candidate') return { label: 'Select model', className: styles.statusNeutral }
    if (projection.status === 'error') return { label: 'Lookup failed', className: styles.statusNeutral }
    return { label: 'Unavailable', className: styles.missing }
  }

  const getSavableAvailabilityProjection = (projection = {}) => ({
    modelNumber: projection.modelNumber,
    requestedQuantity: projection.requestedQuantity,
    targetQuantity: projection.targetQuantity,
    status: projection.status,
    statusLabel: projection.statusLabel,
    earliestLabel: projection.earliestLabel,
    currentAvailable: projection.currentAvailable,
    totals: projection.totals,
    candidates: projection.candidates,
    projectedRows: (projection.projectedRows || []).map((row) => ({
      arrival: row.arrival,
      locationNum: row.locationNum,
      planQty: row.planQty,
      projectedAvailable: row.projectedAvailable,
      supportsTarget: row.supportsTarget,
    })),
  })

  const buildQuotePayload = (status = 'Draft') => {
    const userId = authData?.result?._id || authData?.result?.googleId
    return {
      status,
      creator: userId ? [userId] : [],
      rawText,
      pastedTableRows,
      fields: {
        ...fields,
        dateRequiredInput: parsed.dateRequiredInput || '',
      },
      items: quoteLines.map((line) => ({
        modelNumber: line.modelNumber,
        quantity: Number(line.quantity) || 1,
        description: line.description || '',
        productDescription: line.productDescription || '',
        location: line.location || '',
        unitPrice: Number(line.unitPrice) || 0,
        unitCost: line.unitCost || '',
        pastedUnitPrice: Number(line.pastedUnitPrice) || 0,
        lineTotal: Number(line.lineTotal) || 0,
        matchStatus: line.matchStatus,
      })),
      walkUpFloor,
      totals,
      deliveryDecision: effectiveDeliveryDecision,
      availabilityProjections: availabilityProjections.map(getSavableAvailabilityProjection),
    }
  }

  const getSavableQuoteStatus = () => {
    if (['Converted', 'Declined'].includes(savedQuoteStatus)) return savedQuoteStatus
    return 'Draft'
  }

  const saveQuote = async () => {
    if (!quoteLines.length) {
      setMessage('Extract at least one model before saving a quote.')
      return null
    }

    setIsSavingQuote(true)
    setSaveStatus('')
    try {
      const payload = buildQuotePayload(getSavableQuoteStatus())
      const { data } = savedQuoteId
        ? await updateQuote(savedQuoteId, payload)
        : await addQuote(payload)
      setSavedQuoteId(data._id || '')
      setSavedQuoteNumber(data.quoteNumber || '')
      setSavedQuoteStatus(data.status || 'Draft')
      setSaveStatus(`Saved ${data.quoteNumber || 'quote'}.`)
      if (!savedQuoteId && data._id) history.replace(`/quote-builder/${data._id}`)
      return data
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Unable to save quote.'
      setMessage(errorMessage)
      setSaveStatus(errorMessage)
      return null
    } finally {
      setIsSavingQuote(false)
    }
  }

  const declineQuote = async () => {
    if (!quoteLines.length) {
      setMessage('Extract at least one model before declining a quote.')
      setDeclineDialogOpen(false)
      return null
    }
    if (savedQuoteStatus === 'Converted') {
      setMessage('Converted quotes cannot be declined.')
      setDeclineDialogOpen(false)
      return null
    }

    setIsSavingQuote(true)
    setSaveStatus('')
    try {
      const payload = buildQuotePayload('Declined')
      const { data } = savedQuoteId
        ? await updateQuote(savedQuoteId, payload)
        : await addQuote(payload)
      setSavedQuoteId(data._id || '')
      setSavedQuoteNumber(data.quoteNumber || '')
      setSavedQuoteStatus(data.status || 'Declined')
      setSaveStatus(`Declined ${data.quoteNumber || 'quote'}.`)
      setDeclineDialogOpen(false)
      if (!savedQuoteId && data._id) history.replace(`/quote-builder/${data._id}`)
      return data
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Unable to decline quote.'
      setMessage(errorMessage)
      setSaveStatus(errorMessage)
      return null
    } finally {
      setIsSavingQuote(false)
    }
  }

  const openAsQuote = async () => {
    if (!quoteLines.length) {
      setMessage('Extract at least one model before creating a quote.')
      return
    }

    const activeQuote = await saveQuote()
    if (!activeQuote?._id) return

    const shipTo = fields.address ? parseAddressForShipTo(fields.address) : null
    const receivedByDateInput = fields.earliestFreeDeliveryDate || derivedDeliveryDateInput || parsed.dateRequiredInput
    const receivedByDate = receivedByDateInput
      ? moment(receivedByDateInput).toISOString()
      : undefined
    const walkUpCharges = buildWalkUpChargesFromTotals(totals)
    const walkUpChargeQuantity = walkUpCharges.length
      ? walkUpCharges.reduce((sum, charge) => sum + (Number(charge.quantity) || 0), 0)
      : (requiresWalkUp && totals.walkUpTotal ? totals.itemCount : 1)
    const walkUpChargeTotal = walkUpCharges.length
      ? walkUpCharges.reduce((sum, charge) => sum + (Number(charge.total) || 0), 0)
      : (requiresWalkUp ? totals.walkUpTotal : 0)
    const walkUpLabel = walkUpCharges.length === 1
      ? walkUpCharges[0].walkUp
      : walkUpCharges.length > 1
        ? MIXED_WALK_UP_LABEL
        : totals.walkUpLabel || walkUpFloor || MIXED_WALK_UP_LABEL
    const quoteDraft = {
      source: 'saved-quote',
      type: 'Quotation',
      status: 'Unpaid',
      shipTo,
      receivedByDate,
      quoteId: activeQuote._id,
      quoteNumber: activeQuote.quoteNumber || savedQuoteNumber || undefined,
      notes: buildQuoteSourceNotes({
        fields,
        parsed,
        missingModels: totals.missingModels,
        totals,
        walkUpFloor,
        deliveryDecision: effectiveDeliveryDecision,
        availabilityProjections,
        quoteNumber: activeQuote.quoteNumber || savedQuoteNumber,
      }),
      walkUp: requiresWalkUp ? walkUpLabel : '',
      walkUpCharges,
      walkUpQuantity: requiresWalkUp && walkUpChargeTotal ? walkUpChargeQuantity : 1,
      walkupHaulawayUnits: requiresWalkUp && walkUpChargeTotal ? walkUpChargeQuantity : 1,
      walkUpFee: requiresWalkUp ? walkUpChargeTotal : 0,
      deliveryFee: totals.deliveryFee,
      deliveryFeeOverride: Boolean(totals.deliveryFee),
      items: quoteLines.map((line) => ({
        modelNumber: line.modelNumber,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        unitCost: line.unitCost,
        discount: '',
        amount: '',
        doNotOrder: false,
        lockup: false,
      })),
    }

    history.push('/invoice', { quoteDraft })
  }

  const downloadQuotePdf = async () => {
    if (!quoteLines.length) {
      setMessage('Extract at least one model before generating a quote PDF.')
      return
    }

    setIsGeneratingQuotePdf(true)
    try {
      const activeQuote = await saveQuote()
      if (!activeQuote?._id) return
      const { data } = await createQuotePdf({ quoteId: activeQuote._id })
      openPdfBlob(data)
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Unable to generate quote PDF.')
    } finally {
      setIsGeneratingQuotePdf(false)
    }
  }

  return (
    <div className={styles.page}>
      <Dialog open={declineDialogOpen} onClose={() => setDeclineDialogOpen(false)}>
        <DialogTitle>Decline Quote</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Mark {savedQuoteNumber || 'this quote'} as declined? It will remain saved and visible in the quotes list with a Declined status.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={declineQuote} color="secondary" variant="contained">
            Decline
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={addItemDialogOpen} onClose={closeAddItemDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Product to Quote</DialogTitle>
        <DialogContent>
          <div className={styles.addItemGrid}>
            <TextField
              label="Model number"
              variant="outlined"
              size="small"
              value={newQuoteItem.modelNumber}
              onChange={(event) => setNewQuoteItem((prev) => ({ ...prev, modelNumber: event.target.value }))}
            />
            <TextField
              label="Quantity"
              type="number"
              variant="outlined"
              size="small"
              value={newQuoteItem.quantity}
              onChange={(event) => setNewQuoteItem((prev) => ({ ...prev, quantity: event.target.value }))}
            />
            <TextField
              label="Location"
              variant="outlined"
              size="small"
              value={newQuoteItem.location}
              onChange={(event) => setNewQuoteItem((prev) => ({ ...prev, location: event.target.value }))}
            />
            <TextField
              label="Manual unit price"
              variant="outlined"
              size="small"
              value={newQuoteItem.unitPrice}
              onChange={(event) => setNewQuoteItem((prev) => ({ ...prev, unitPrice: event.target.value }))}
              helperText="Used only when the model is not matched in the product catalog."
            />
            <TextField
              className={styles.fullSpan}
              label="Description"
              variant="outlined"
              size="small"
              value={newQuoteItem.description}
              onChange={(event) => setNewQuoteItem((prev) => ({ ...prev, description: event.target.value }))}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddItemDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={addQuoteItem} color="primary" variant="contained">
            Add Product to Quote
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={catalogProductDialogOpen} onClose={closeCatalogProductDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Product</DialogTitle>
        <DialogContent>
          <div className={styles.addItemGrid}>
            <TextField
              label="Brand"
              variant="outlined"
              size="small"
              value={newCatalogProduct.brand}
              onChange={(event) => setNewCatalogProduct((prev) => ({ ...prev, brand: event.target.value }))}
            />
            <TextField
              label="Model number"
              variant="outlined"
              size="small"
              value={newCatalogProduct.modelNumber}
              onChange={(event) => setNewCatalogProduct((prev) => ({ ...prev, modelNumber: event.target.value }))}
            />
            <TextField
              label="Cost"
              variant="outlined"
              size="small"
              value={newCatalogProduct.cost}
              placeholder="$0.00"
              inputProps={{ inputMode: 'decimal' }}
              onChange={(event) => setNewCatalogProduct((prev) => ({ ...prev, cost: event.target.value }))}
              onBlur={() => setNewCatalogProduct((prev) => ({ ...prev, cost: formatCurrencyInput(prev.cost) }))}
            />
            <TextField
              label="Price"
              variant="outlined"
              size="small"
              value={newCatalogProduct.price}
              placeholder="$0.00"
              inputProps={{ inputMode: 'decimal' }}
              onChange={(event) => setNewCatalogProduct((prev) => ({ ...prev, price: event.target.value }))}
              onBlur={() => setNewCatalogProduct((prev) => ({ ...prev, price: formatCurrencyInput(prev.price) }))}
            />
            <TextField
              label="Type"
              variant="outlined"
              size="small"
              value={newCatalogProduct.type}
              onChange={(event) => setNewCatalogProduct((prev) => ({ ...prev, type: event.target.value }))}
            />
            <TextField
              className={styles.fullSpan}
              label="Description"
              variant="outlined"
              size="small"
              value={newCatalogProduct.description}
              onChange={(event) => setNewCatalogProduct((prev) => ({ ...prev, description: event.target.value }))}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCatalogProductDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={saveCatalogProduct} color="primary" variant="contained" disabled={isSavingCatalogProduct}>
            {isSavingCatalogProduct ? 'Saving...' : 'Add Product'}
          </Button>
        </DialogActions>
      </Dialog>
      <h1 className={styles.pageTitle}>
        Quote Builder{savedQuoteNumber ? ` - ${savedQuoteNumber}` : ''}
      </h1>

      <div className={styles.workspace}>
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>Email/table input</h2>
              <p>Click the paste area and paste the email/table. The raw pasted text stays hidden.</p>
            </div>
          </div>
          <div
            ref={pasteTargetRef}
            className={`${styles.pasteCanvas} ${pastedTableRows.length ? styles.pasteCanvasFilled : ''}`}
            tabIndex={0}
            role="textbox"
            aria-label="Paste quote email or table"
            onPaste={handlePaste}
            onClick={() => (pasteInputRef.current || pasteTargetRef.current)?.focus()}
          >
            {pastedTableRows.length ? (
              <div className={styles.formattedPaste}>
                <div className={styles.formattedPasteHeader}>
                  <h3>Formatted paste</h3>
                  <span>{pastedTableRows.length} rows reconstructed</span>
                </div>
                <div className={styles.tableWrap}>
                  <table className={styles.pastePreviewTable}>
                    <tbody>
                      {pastedTableRows.map((row, rowIndex) => (
                        <tr key={`paste-row-${rowIndex}`}>
                          {row.map((cell, cellIndex) => {
                            const isHeaderRow = row.some((value) => /^(location|quantity|full description|category|unit price|price)$/i.test(String(value || '').trim()))
                            const isMetadataLabel = METADATA_LABEL_CELL_REGEX.test(String(cell || '').trim()) && cellIndex % 2 === 0
                            const Cell = isHeaderRow || isMetadataLabel ? 'th' : 'td'
                            return (
                              <Cell key={`paste-cell-${rowIndex}-${cellIndex}`}>
                                <input
                                  className={styles.pasteCellInput}
                                  value={cell}
                                  aria-label={`Pasted table row ${rowIndex + 1} cell ${cellIndex + 1}`}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={(event) => updatePastedTableCell(rowIndex, cellIndex, event.target.value)}
                                />
                              </Cell>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <>
                <textarea
                  ref={pasteInputRef}
                  className={styles.pasteInput}
                  aria-label="Paste quote email or table"
                  placeholder={'Tap here, then long-press and choose Paste. The pasted table will format automatically.'}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  onPaste={handlePaste}
                  onChange={handlePasteInputChange}
                />
              </>
            )}
          </div>
          <div className={styles.actions}>
            <Button color="primary" variant="contained" onClick={extractQuote}>
              Re-run Extraction
            </Button>
            <Button variant="outlined" onClick={() => setAddItemDialogOpen(true)}>
              Add Product to Quote
            </Button>
            <Button variant="outlined" onClick={clearQuote}>
              Clear
            </Button>
          </div>
          {message && <div className={styles.warning}>{message}</div>}
          {saveStatus && <div className={styles.success}>{saveStatus}</div>}
        </section>

        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>Quote preview</h2>
              <p>Review the extracted fields and catalog matches before sending it to the invoice builder.</p>
            </div>
            <div className={styles.headerActions}>
              <Button variant="outlined" onClick={() => setCatalogProductDialogOpen(true)}>
                Add Product
              </Button>
              <Button variant="outlined" disabled={!quoteLines.length || isSavingQuote} onClick={saveQuote}>
                {isSavingQuote ? 'Saving...' : savedQuoteId ? 'Save Changes' : 'Save Quote'}
              </Button>
              <Button variant="outlined" disabled={!quoteLines.length || isSavingQuote || isGeneratingQuotePdf} onClick={downloadQuotePdf}>
                {isGeneratingQuotePdf ? 'Generating...' : 'Download PDF'}
              </Button>
              <Button
                color="secondary"
                variant="contained"
                disabled={!quoteLines.length || isSavingQuote || isGeneratingQuotePdf || savedQuoteStatus === 'Converted' || savedQuoteStatus === 'Declined'}
                onClick={() => setDeclineDialogOpen(true)}
              >
                {savedQuoteStatus === 'Declined' ? 'Declined' : 'Decline Quote'}
              </Button>
              <Button color="primary" variant="contained" disabled={!quoteLines.length || isLoading || isSavingQuote || isGeneratingQuotePdf} onClick={openAsQuote}>
                Convert to Invoice
              </Button>
            </div>
          </div>

          <div className={styles.extractedGrid}>
            <TextField
              className={styles.fullSpan}
              label="Ship to address"
              variant="outlined"
              size="small"
              value={fields.address}
              onChange={(event) => setFields((prev) => ({ ...prev, address: event.target.value }))}
            />
            <TextField
              label="Quote date"
              type="date"
              variant="outlined"
              size="small"
              value={fields.dateAcquired}
              onChange={(event) => setFields((prev) => ({ ...prev, dateAcquired: event.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Date required / delivery"
              variant="outlined"
              size="small"
              value={fields.dateRequired}
              onChange={(event) => setFields((prev) => ({ ...prev, dateRequired: event.target.value }))}
            />
            <TextField
              label="Reason for purchase"
              variant="outlined"
              size="small"
              value={fields.reasonForPurchase}
              onChange={(event) => setFields((prev) => ({ ...prev, reasonForPurchase: event.target.value }))}
            />
            <TextField
              label="Walk up / elevator"
              variant="outlined"
              size="small"
              value={fields.walkUp}
              onChange={(event) => setFields((prev) => ({ ...prev, walkUp: event.target.value }))}
            />
            {requiresWalkUp ? (
              <TextField
                label="Fallback walk-up floor"
                select
                variant="outlined"
                size="small"
                value={walkUpFloor}
                onChange={(event) => setWalkUpFloor(event.target.value)}
                helperText="Used only when a floor cannot be inferred from locations like APT E51."
              >
                <MenuItem value="">Select floor</MenuItem>
                {WALK_UP_FLOORS.map((floor) => (
                  <MenuItem key={floor} value={floor}>
                    {floor} - {dollarFormatter.format(WALK_UP_FEES[floor])}/unit
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <TextField
              label="Delivery note"
              variant="outlined"
              size="small"
              value={fields.deliveryNote}
              onChange={(event) => setFields((prev) => ({ ...prev, deliveryNote: event.target.value }))}
            />
            <TextField
              label="Delivery fee"
              variant="outlined"
              size="small"
              value={fields.deliveryFee}
              placeholder="$0.00"
              inputProps={{ inputMode: 'decimal' }}
              onChange={(event) => setFields((prev) => ({ ...prev, deliveryFee: event.target.value }))}
              onBlur={() => setFields((prev) => ({ ...prev, deliveryFee: formatCurrencyInput(prev.deliveryFee) }))}
            />
            <div className={`${styles.deliveryOverridePanel} ${styles.fullSpan}`}>
              <div className={styles.deliveryOverrideHeader}>
                <div>
                  <strong>Earliest free delivery</strong>
                  <span>Inventory-derived value: {getQuoteDeliverySentence(deliveryDecision)}</span>
                </div>
              </div>
              <div className={styles.deliveryOverrideControls}>
                <TextField
                  label="Selected delivery date"
                  type="date"
                  variant="outlined"
                  size="small"
                  value={fields.earliestFreeDeliveryDate || derivedDeliveryDateInput}
                  disabled={fields.deliveryNoDate}
                  onChange={(event) => setFields((prev) => ({
                    ...prev,
                    earliestFreeDeliveryDate: event.target.value,
                    deliveryNoDate: false,
                  }))}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControlLabel
                  className={styles.noDateCheck}
                  control={(
                    <Checkbox
                      color="primary"
                      checked={Boolean(fields.deliveryNoDate)}
                      onChange={(event) => setFields((prev) => ({
                        ...prev,
                        deliveryNoDate: event.target.checked,
                        earliestFreeDeliveryDate: event.target.checked ? '' : prev.earliestFreeDeliveryDate,
                      }))}
                    />
                  )}
                  label="No date with reason"
                />
                {fields.deliveryNoDate ? (
                  <TextField
                    label="Reason"
                    variant="outlined"
                    size="small"
                    value={fields.deliveryNoDateReason}
                    onChange={(event) => setFields((prev) => ({ ...prev, deliveryNoDateReason: event.target.value }))}
                  />
                ) : null}
              </div>
              <p>{deliverySentence}</p>
            </div>
          </div>

          <div className={styles.statStrip}>
            <div className={styles.statCard}>
              <span>Units</span>
              <strong>{totals.itemCount}</strong>
            </div>
            <div className={styles.statCard}>
              <span>Matched</span>
              <strong>{quoteLines.length - totals.missingModels.length}/{quoteLines.length}</strong>
            </div>
            <div className={styles.statCard}>
              <span>Product subtotal</span>
              <strong>{dollarFormatter.format(totals.itemSubtotal)}</strong>
            </div>
            <div className={styles.statCard}>
              <span>Walk-up</span>
              <strong>{requiresWalkUp && totals.unresolvedWalkUpQuantity ? 'Select fallback floor' : dollarFormatter.format(totals.walkUpTotal)}</strong>
            </div>
            <div className={styles.statCard}>
              <span>Delivery fee</span>
              <strong>{dollarFormatter.format(totals.deliveryFee)}</strong>
            </div>
            <div className={styles.statCard}>
              <span>Quote total</span>
              <strong>{dollarFormatter.format(totals.subtotal)}</strong>
            </div>
          </div>

          {requiresWalkUp ? (
            <div className={styles.walkUpBreakdown}>
              <div className={styles.walkUpBreakdownHeader}>
                <strong>Walk-up floor breakdown</strong>
                <span>
                  {totals.unresolvedWalkUpQuantity
                    ? `${totals.unresolvedWalkUpQuantity} unit(s) need fallback`
                    : 'Floors inferred from item locations when possible'}
                </span>
              </div>
              <div className={styles.walkUpBreakdownRows}>
                {totals.walkUpBreakdown.length ? (
                  totals.walkUpBreakdown.map((row) => (
                    <div
                      key={`${row.displayFloorLabel}-${row.rate}`}
                      className={`${styles.walkUpBreakdownRow} ${row.needsFallback ? styles.walkUpNeedsFallback : ''}`}
                    >
                      <span>{row.displayFloorLabel || row.floorLabel || 'Needs floor'}</span>
                      <span>{row.quantity} unit(s)</span>
                      <span>{row.rate ? `${dollarFormatter.format(row.rate)}/unit` : 'No charge'}</span>
                      <strong>{row.needsFallback ? 'Needs fallback' : dollarFormatter.format(row.total)}</strong>
                    </div>
                  ))
                ) : (
                  <div className={styles.walkUpBreakdownRow}>
                    <span>No walk-up units found.</span>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {quoteLines.length ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Qty</th>
                    <th>Model</th>
                    <th>Catalog price</th>
                    <th>Email price</th>
                    <th>Total</th>
                    {requiresWalkUp ? <th>Walk-up</th> : null}
                    <th>Status</th>
                    <th>Availability</th>
                  </tr>
                </thead>
                <tbody>
                  {quoteLines.map((line, index) => {
                    const walkUpCharge = getLineWalkUpCharge(line, walkUpFloor)
                    const availability = getLineAvailabilityPill(line)
                    return (
                      <tr key={`${line.modelNumber}-${index}`}>
                        <td>{line.quantity}</td>
                        <td className={styles.modelCell}>
                          <strong>{line.modelNumber}</strong>
                          <span>{line.productDescription || line.description || 'No description'}</span>
                          {line.location ? <span>{line.location}</span> : null}
                        </td>
                        <td>{line.matchedProduct ? dollarFormatter.format(line.unitPrice) : 'Not found'}</td>
                        <td>{line.pastedUnitPrice ? dollarFormatter.format(line.pastedUnitPrice) : '-'}</td>
                        <td>{dollarFormatter.format(line.lineTotal)}</td>
                        {requiresWalkUp ? (
                          <td>
                            <span className={`${styles.pill} ${walkUpCharge.needsFallback ? styles.missing : styles.matched}`}>
                              {walkUpCharge.displayFloorLabel}
                            </span>
                          </td>
                        ) : null}
                        <td>
                          <span className={`${styles.pill} ${line.matchedProduct ? styles.matched : styles.missing}`}>
                            {line.matchedProduct ? 'Matched' : 'Missing'}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles.pill} ${availability.className}`}>
                            {availability.label}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.empty}>Paste an email/table and click Extract Quote to preview the quote.</div>
          )}

          {totals.missingModels.length ? (
            <div className={styles.warning}>
              Missing product catalog matches: {totals.missingModels.join(', ')}. Those lines will use the pasted price if one exists.
            </div>
          ) : null}

          {quoteLines.length ? (
            <div className={styles.inventoryProof}>
              <div className={styles.inventoryProofHeader}>
                <div>
                  <h3>Delivery availability proof</h3>
                  <p>Availability uses current PC inventory and scheduled purchase orders to determine the earliest free delivery date.</p>
                </div>
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={!inventoryRequests.length || inventoryLookupInProgress}
                  onClick={lookupAllInventory}
                >
                  {inventoryLookupInProgress ? (
                    <span className={styles.buttonLoadingContent}>
                      <CircularProgress size={16} color="inherit" />
                      Checking availability
                    </span>
                  ) : 'Refresh Availability'}
                </Button>
              </div>

              <div className={styles.deliveryDecisionCard}>
                <span>Earliest delivery date</span>
                <strong>{deliverySentence}</strong>
                <p>
                  If every model is available now, the quote can ship now. Otherwise this uses the latest date
                  required across all model projections unless manually overridden above.
                </p>
              </div>

              <div className={styles.inventoryCardGrid}>
                {availabilityProjections.map((projection) => {
                  const lookupState = inventoryLoadingByModel[projection.modelNumber]
                  const loading = lookupState === 'active' || lookupState === true
                  const queued = lookupState === 'queued'
                  const totalsForModel = projection.totals || {}
                  const statusLabel = loading ? 'Checking' : queued ? 'Queued' : projection.statusLabel

                  return (
                    <article className={styles.inventoryCard} key={projection.modelNumber}>
                      <div className={styles.inventoryCardHeader}>
                        <div>
                          <h4>{projection.modelNumber}</h4>
                          <p>{inventoryRequests.find((request) => request.modelNumber === projection.modelNumber)?.description || 'No description'}</p>
                        </div>
                        <span className={`${styles.statusPill} ${getProjectionStatusClass(projection.status)} ${loading || queued ? styles.statusLoading : ''}`}>
                          {loading ? <CircularProgress size={13} color="inherit" /> : null}
                          {statusLabel}
                        </span>
                      </div>

                      <div className={styles.lookupValueGrid}>
                        <div>
                          <span>Available</span>
                          <strong>{projection.currentAvailable ?? '-'}</strong>
                        </div>
                        <div>
                          <span>Requested</span>
                          <strong>{projection.requestedQuantity}</strong>
                        </div>
                        <div>
                          <span>Earliest</span>
                          <strong>{projection.earliestLabel}</strong>
                        </div>
                        <div>
                          <span>Fdale qty</span>
                          <strong>{totalsForModel.farmingdaleQuantity ?? '-'}</strong>
                        </div>
                        <div>
                          <span>NJ qty</span>
                          <strong>{totalsForModel.njQuantity ?? '-'}</strong>
                        </div>
                        <div>
                          <span>Scheduled</span>
                          <strong>{totalsForModel.scheduled ?? '-'}</strong>
                        </div>
                        <div>
                          <span>ASAP</span>
                          <strong>{totalsForModel.asap ?? '-'}</strong>
                        </div>
                        <div>
                          <span>On order</span>
                          <strong>{totalsForModel.onOrder ?? '-'}</strong>
                        </div>
                      </div>

                      {projection.candidates?.length ? (
                        <div className={styles.candidateNotice}>
                          <strong>Select exact PC model:</strong>
                          <div className={styles.candidateList}>
                            {projection.candidates.map((candidate) => {
                              const candidateModel = getCandidateModelNumber(candidate)
                              if (!candidateModel) return null
                              return (
                                <button
                                  type="button"
                                  key={`${projection.modelNumber}-${candidateModel}`}
                                  onClick={() => selectModelCandidate(projection.modelNumber, candidateModel)}
                                >
                                  <strong>{candidateModel}</strong>
                                  {candidate.description || candidate.modelDescription ? (
                                    <span>{candidate.description || candidate.modelDescription}</span>
                                  ) : null}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      ) : null}

                      <div className={styles.scheduledTableBlock}>
                        <h5>Next scheduled deliveries</h5>
                        {projection.projectedRows.length ? (
                          <div className={styles.tableWrap}>
                            <table className={styles.deliveryTable}>
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Location</th>
                                  <th>Qty</th>
                                  <th>Projected available</th>
                                  <th>Used for date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {projection.projectedRows.map((row, index) => (
                                  <tr
                                    key={`${projection.modelNumber}-${row.arrival}-${row.locationNum}-${index}`}
                                    className={row.supportsTarget ? styles.supportsTargetRow : ''}
                                  >
                                    <td>{formatPcArrivalDate(row.arrival)}</td>
                                    <td>{row.locationNum || '-'}</td>
                                    <td>{row.planQty}</td>
                                    <td>{row.projectedAvailable}</td>
                                    <td>{row.supportsTarget ? 'Yes' : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className={styles.emptyDeliveryRows}>
                            {projection.status === 'not-checked'
                              ? 'Run the lookup to see scheduled deliveries.'
                              : 'No scheduled deliveries returned for this model.'}
                          </div>
                        )}
                      </div>
                    </article>
                  )
                })}
              </div>
            </div>
          ) : null}

          {quoteLines.length ? (
            <div className={styles.emailTextCard}>
              <div className={styles.emailTextHeader}>
                <div>
                  <h3>Email response text</h3>
                  <p>Edit this if needed, then copy it into your email response.</p>
                </div>
                <div className={styles.emailTextActions}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEmailTextEdited(false)
                      setEmailText(generatedEmailText)
                    }}
                  >
                    Reset
                  </Button>
                  <Button color="primary" variant="contained" onClick={copyEmailText}>
                    Copy Text
                  </Button>
                </div>
              </div>
              <TextField
                className={styles.emailTextField}
                variant="outlined"
                multiline
                rows={6}
                value={emailText}
                onChange={(event) => {
                  setEmailText(event.target.value)
                  setEmailTextEdited(true)
                }}
              />
            </div>
          ) : null}
        </section>
      </div>
    </div>
  )
}

export default QuoteBuilderPage
